// src/pages/portal/PortalInvoices.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { usePortalAuth } from '../../context/PortalAuthContext';
import PortalLayout from '../../components/PortalLayout';

function StatusBadge({ status }) {
  const map = {
    paid:    { bg: '#e8f7ee', color: '#16a34a', label: 'Paid' },
    unpaid:  { bg: '#fff8e1', color: '#d97706', label: 'Unpaid' },
    overdue: { bg: '#fdf0f0', color: '#dc2626', label: 'Overdue' },
    partial: { bg: '#e8f0ff', color: '#2563eb', label: 'Partial' },
    draft:   { bg: '#f0f0ec', color: '#666',    label: 'Pending' },
    sent:    { bg: '#fff8e1', color: '#d97706', label: 'Due' },
  };
  const s = map[status?.toLowerCase()] || { bg: '#f0f0ec', color: '#666', label: status };
  return (
    <span style={{ background: s.bg, color: s.color, padding: '3px 9px', borderRadius: '12px', fontSize: '11.5px', fontWeight: '600' }}>
      {s.label}
    </span>
  );
}

function fmt(amount) {
  if (amount == null) return '—';
  return '$' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function PortalInvoices() {
  const { clientRecord } = usePortalAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!clientRecord?.id) return;
    supabase
      .from('invoices')
      .select('id, title, invoice_number, amount, total, status, due_date, created_at')
      .eq('client_id', clientRecord.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data) setInvoices(data);
        setLoading(false);
      });
  }, [clientRecord]);

  const filters = ['all', 'unpaid', 'overdue', 'paid'];
  const visible = filter === 'all' ? invoices : invoices.filter(i => i.status?.toLowerCase() === filter);

  // Total amount owed — use total || amount
  const totalOwed = invoices
    .filter(i => ['unpaid', 'overdue', 'partial', 'sent'].includes(i.status?.toLowerCase()))
    .reduce((sum, i) => sum + Number(i.total || i.amount || 0), 0);

  return (
    <PortalLayout>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '600', color: '#1a1a18', margin: 0 }}>Your Invoices</h1>
        <p style={{ fontSize: '13px', color: '#888', margin: '6px 0 0' }}>View invoice details, check payment status, and download PDFs.</p>
      </div>

      {/* Balance summary */}
      {totalOwed > 0 && (
        <div style={{ background: '#fff8e1', border: '1px solid #fde68a', borderRadius: '8px', padding: '14px 18px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '14px', color: '#92400e', fontWeight: '500' }}>Outstanding Balance</span>
          <span style={{ fontSize: '20px', fontWeight: '700', color: '#92400e' }}>{fmt(totalOwed)}</span>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
        {filters.map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '6px 14px', borderRadius: '20px', border: '1px solid',
            borderColor: filter === f ? '#1a1a18' : '#e0dbd0',
            background: filter === f ? '#1a1a18' : 'transparent',
            color: filter === f ? '#e8e4d9' : '#666',
            fontSize: '12.5px', fontWeight: filter === f ? '600' : '400',
            cursor: 'pointer', textTransform: 'capitalize', fontFamily: 'inherit',
          }}>
            {f === 'all'
              ? `All (${invoices.length})`
              : `${f.charAt(0).toUpperCase() + f.slice(1)} (${invoices.filter(i => i.status?.toLowerCase() === f).length})`
            }
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: '#aaa', fontSize: '14px' }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid #e8e4dc', borderRadius: '8px', padding: '40px', textAlign: 'center', color: '#aaa', fontSize: '14px' }}>
          No {filter !== 'all' ? filter : ''} invoices found.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e8e4dc', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 110px 90px 60px', padding: '10px 18px', background: '#f8f6f2', borderBottom: '1px solid #e8e4dc', fontSize: '11px', color: '#999', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: '600' }}>
            <span>Invoice</span><span>Due Date</span><span>Amount</span><span>Status</span><span></span>
          </div>
          {visible.map((inv, i) => (
            <div key={inv.id} style={{
              display: 'grid', gridTemplateColumns: '1fr 100px 110px 90px 60px',
              padding: '14px 18px', borderBottom: i < visible.length - 1 ? '1px solid #f0ece4' : 'none',
              alignItems: 'center',
            }}>
              <div>
                <Link to={`/portal/invoices/${inv.id}`} style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a18', textDecoration: 'none' }}>
                  {inv.title || `Invoice #${inv.invoice_number || inv.id?.slice(0,6)}`}
                </Link>
              </div>
              <div style={{ fontSize: '13px', color: '#777' }}>{fmtDate(inv.due_date)}</div>
              {/* Use total || amount for compatibility with old and new records */}
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a18' }}>{fmt(inv.total || inv.amount)}</div>
              <div><StatusBadge status={inv.status} /></div>
              <div><Link to={`/portal/invoices/${inv.id}`} style={{ fontSize: '12.5px', color: '#c8a96e', textDecoration: 'none', fontWeight: '500' }}>View →</Link></div>
            </div>
          ))}
        </div>
      )}
    </PortalLayout>
  );
}
